<template>
  <View direction="column" gap="sm" class="message-feedback" :class="{ 'is-engaged': isEngaged }">
    <View gap="xs" align="center" class="rating-bar">
      <IconButton
        icon="thumbs-up"
        variant="ghost"
        size="small"
        class="rating-button"
        :class="{ 'is-up': rating === 'up' }"
        :disabled="isSubmitting"
        title="Good reply"
        aria-label="Good reply"
        :aria-pressed="rating === 'up'"
        @click="rateUp"
      />

      <IconButton
        icon="thumbs-down"
        variant="ghost"
        size="small"
        class="rating-button"
        :class="{ 'is-down': rating === 'down' }"
        :disabled="isSubmitting"
        title="I wouldn't have said that"
        aria-label="Flag this reply"
        :aria-pressed="rating === 'down'"
        @click="rateDown"
      />

      <Text v-if="!savedFeedback" variant="body-small" color="tertiary" class="rating-hint">
        Rate this reply. Internal only.
      </Text>
    </View>

    <Card v-if="isComposerOpen" variant="tertiary" size="medium" class="composer">
      <View direction="column" gap="md">
        <Text variant="label-medium">What was off?</Text>

        <View gap="sm" wrap="wrap">
          <Chip
            v-for="category in categories"
            :key="category.value"
            :is-active="selectedCategories.includes(category.value)"
            @click="toggleCategory(category.value)"
          >
            {{ category.label }}
          </Chip>
        </View>

        <Textarea
          ref="commentInputRef"
          v-model="comment"
          rows="3"
          resize="vertical"
          placeholder="What would you have said instead?"
        />

        <View gap="sm" align="center" justify="space-between" wrap="wrap">
          <View gap="xs" align="center" class="disclaimer">
            <Icon name="lock-01" size="extra-small" />
            <Text variant="body-small" color="secondary">Only your team sees this. It helps Tallo improve.</Text>
          </View>

          <View gap="sm" align="center">
            <Button variant="ghost" size="small" :disabled="isSubmitting" @click="cancel">Cancel</Button>
            <Button variant="primary" size="small" :loading="isSubmitting" @click="submit">Share feedback</Button>
          </View>
        </View>
      </View>
    </Card>
  </View>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';

import { Button, Card, Chip, Icon, IconButton, Text, Textarea, View } from '@tallo/design-system';

import {
  MessageFeedbackCategory,
  MessageFeedbackDto,
  MessageFeedbackRating,
  messageFeedbackCategoryLabels,
} from '../models/message-feedback.interface';
import { useSubmitMessageFeedbackMutation } from '../mutations/submit-message-feedback.mutation';

const props = defineProps<{ messageId: number; feedback?: MessageFeedbackDto | null }>();

const categories = Object.values(MessageFeedbackCategory).map((value) => ({
  value,
  label: messageFeedbackCategoryLabels[value],
}));

// What is actually stored in the DB right now (props seed it, successful submits update it). The UI
// `rating` below can drift from it — e.g. a thumbs-down click shows 'down' before anything is sent.
const savedFeedback = ref<MessageFeedbackDto | null>(props.feedback ?? null);

const rating = ref<MessageFeedbackRating | null>(props.feedback?.rating ?? null);
const selectedCategories = ref<MessageFeedbackCategory[]>([...(props.feedback?.categories ?? [])]);
const comment = ref(props.feedback?.comment ?? '');
const isComposerOpen = ref(false);
const commentInputRef = ref<InstanceType<typeof Textarea>>();

const { mutateAsync: submitFeedback, isLoading: isSubmitting } = useSubmitMessageFeedbackMutation();

// Keep the bar pinned open once the landlord has engaged with it, otherwise it fades out from under the
// cursor the moment they move away from the message.
const isEngaged = computed(() => isComposerOpen.value || rating.value !== null);

function toggleCategory(category: MessageFeedbackCategory): void {
  const index = selectedCategories.value.indexOf(category);

  if (index === -1) {
    selectedCategories.value.push(category);
  } else {
    selectedCategories.value.splice(index, 1);
  }
}

async function rateUp(): Promise<void> {
  // There is no way to retract a rating, so a second click is a no-op rather than a silent re-send.
  if (rating.value === 'up') {
    return;
  }

  isComposerOpen.value = false;
  rating.value = 'up';

  // 'up' is already stored (e.g. up → thumbs-down opened but abandoned → up again): restore the UI
  // without re-sending identical feedback.
  if (savedFeedback.value?.rating === 'up') {
    return;
  }

  try {
    await submitFeedback({ messageId: props.messageId, rating: 'up' });
    savedFeedback.value = { rating: 'up', categories: [], comment: null };
  } catch {
    // The mutation already surfaces the error as a toast; roll the button back so it doesn't claim a
    // rating that was never stored.
    rating.value = savedFeedback.value?.rating ?? null;
  }
}

// Thumbs-down sends nothing until the composer is submitted, so a misclick costs the landlord nothing.
function rateDown(): void {
  rating.value = 'down';
  isComposerOpen.value = true;

  nextTick(() => (commentInputRef.value?.$el as HTMLTextAreaElement | undefined)?.focus());
}

function cancel(): void {
  isComposerOpen.value = false;

  // Nothing was sent, so leave no trace of the thumbs-down that opened this.
  if (savedFeedback.value?.rating !== 'down') {
    rating.value = savedFeedback.value?.rating ?? null;
    selectedCategories.value = [];
    comment.value = '';
  }
}

async function submit(): Promise<void> {
  try {
    await submitFeedback({
      messageId: props.messageId,
      rating: 'down',
      categories: [...selectedCategories.value],
      comment: comment.value.trim() || null,
    });

    savedFeedback.value = {
      rating: 'down',
      categories: [...selectedCategories.value],
      comment: comment.value.trim() || null,
    };
    isComposerOpen.value = false;
  } catch {
    // Keep the composer open on failure so the typed feedback isn't lost.
  }
}
</script>

<style scoped lang="scss">
// Selectors are deliberately deep enough to outrank IconButton's own `.button.ghost` rule, which lands
// at the same specificity as a single scoped class would.
.message-feedback .rating-bar .rating-button {
  color: var(--t-text-color-tertiary);
  // Tighter than IconButton's 1.75rem `small`: this row is permanently allocated under every Tallo
  // message, so its height is pure spacing between that message and whatever follows it.
  width: 1.5rem;
  height: 1.5rem;

  // The icon SVGs ship with `fill="none"` as a presentation attribute, which any CSS rule outranks —
  // that's what lets the active thumb read as filled without a second set of solid icons.
  &.is-up,
  &.is-down {
    :deep(.svg-icon path) {
      fill: currentColor;
    }
  }

  // A chosen rating is told by the filled glyph and its tint alone — no pill behind it. The bar sits
  // inside the message body, so a standing block of colour there outweighs what it reports. Tints
  // are a step deeper and less saturated than the status palette for the same reason.
  &.is-up {
    color: var(--t-color-green-600);
  }

  &.is-down {
    color: var(--t-color-red-700);
  }
}

// Touch devices keep the rating bar permanently visible, so the coaching hint would be permanent
// noise there — it only makes sense as a desktop hover affordance.
.rating-hint {
  @media (hover: none) {
    display: none;
  }
}

.composer {
  width: 100%;

  :deep(textarea) {
    width: 100%;
  }
}

.disclaimer {
  color: var(--t-text-color-secondary);
  min-width: 0;
}

.disclaimer :deep(.svg-icon) {
  flex-shrink: 0;
}
</style>
